/**
 * Certification Page (EX7)
 * 
 * Process Owner Certification module with role-based views:
 * - Admin View: 4 tabs (Dashboard, Certification, Documents, Change Indicators)
 * - Process Owner View: 4 tabs (Certification, Documents, Change Indicators, Overview)
 * 
 * Key insight: Admin and PO share most functionality - the difference is:
 * - Admin can select any Process Owner to view
 * - PO can only view their own data
 * 
 * Shared components:
 * - CertificationDetailTab: Certification review (Admin Tab 2, PO Tab 1)
 * - ChecklistTab: Change indicators (Admin Tab 4, PO Tab 3)
 * - DocumentsTab: Document repository (Admin Tab 3, PO Tab 2)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Typography, Tabs, Button, message, Tooltip } from 'antd';
import { FilterOutlined, MailOutlined } from '@ant-design/icons';
//import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';
import ListChecksIcon from '../../../assets/icons/ic-list-checks.svg?react';
import AcknowledgmentIcon from '../../../assets/icons/ic-acknowledgment.svg?react';
import DocumentIcon from '../../../assets/icons/ic-document.svg?react';
import ChecklistIcon from '../../../assets/icons/ic-checklist.svg?react';
import ArchiveIcon from '../../../assets/icons/ic-archive.svg?react';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../store';
import {
  setSelectedQuarter,
  setSelectedProcessOwner,
  setProcessOwners,
  setQuarterData,
  setChecklistItems,
  resetDraftChecklist,
  resetPageCache,
  clearCertificationData,
  clearChecklistData,
} from '../../../store/slices/certificationSlice';
import {
  getProcessOwners,
  getCertificationQuarterData,
  getChecklistItems,
  getCurrentQuarter,
  type Quarter,
} from '../../../services/certificationService';

// Tab Components
import AdminDashboardTab from './components/admin/AdminDashboardTab';
import POOverviewTab from './components/processOwner/POOverviewTab';
// Shared Components
import CertificationDetailTab from './components/shared/CertificationDetailTab';
import ChecklistTab from './components/shared/ChecklistTab';
import DocumentsTab from './components/shared/DocumentsTab';

const { Title } = Typography;

// ========================================
// Styled Components
// ========================================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterButtonWrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #E5E7EB;
    border-color: #D1D5DB;
  }

  .anticon {
    font-size: 16px;
    color: #6B7280;
  }
`;

const FilterBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #EF4444;
  border-radius: 50%;
`;

// ========================================
// Component
// ========================================

// Valid tab keys for Admin and Process Owner
const ADMIN_TABS = ['dashboard', 'certification', 'documents', 'checklist'] as const;
const PO_TABS = ['certification', 'documents', 'checklist', 'overview'] as const;

type AdminTabKey = typeof ADMIN_TABS[number];
type POTabKey = typeof PO_TABS[number];

const CertificationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Auth state for role detection
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Filter state from navigation (from Dashboard Metric 12)
  const [filterProcessOwnerId, setFilterProcessOwnerId] = useState<string | null>(null);
  
  // Annual report for API calls
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  // Certification state
  const {
    selectedQuarter,
    selectedProcessOwnerId,
    processOwners,
    lastFetchedReportId,
    hasUnsavedChanges,
  } = useSelector((state: RootState) => state.certification);
  
  // Local loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Export trigger state - passed to child components
  const [triggerExport, setTriggerExport] = useState(false);
  
  // Remind All mode state (Dashboard tab only)
  const [isRemindAllMode, setIsRemindAllMode] = useState(false);
  
  // State for unsaved changes warning (A3.4) - modal is shown in ChecklistTab
  const [pendingTabKey, setPendingTabKey] = useState<string | null>(null);
  
  // Ref to prevent duplicate fetches
  const hasFetchedRef = useRef(false);
  const currentReportIdRef = useRef<string | undefined>(undefined);
  const lastQuarterFetchRef = useRef<string | null>(null);
  
  // isAdmin is computed in authSlice when user is set
  // Process Owner = !isAdmin
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // const isAdmin = false; // Uncomment to test Process Owner view
  
  // Clear cache when leaving the page (unmount)
  // This ensures fresh data is fetched when returning to the page
  // Note: Does NOT clear when switching between tabs within the page
  useEffect(() => {
    return () => {
      // console.log('🧹 CertificationPage: Unmounting - clearing page cache');
      dispatch(resetPageCache());
    };
  }, [dispatch]);
  
  // Read filter from navigation state (from Dashboard Metric 12)
  useEffect(() => {
    const state = location.state as { selectedProcessOwnerId?: string } | null;
    if (state?.selectedProcessOwnerId) {
      setFilterProcessOwnerId(state.selectedProcessOwnerId);
      // Clear the state to prevent re-applying filter on tab changes
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Handler to clear process owner filter
  const handleClearFilter = useCallback(() => {
    setFilterProcessOwnerId(null);
  }, []);
  
  // Get current tab from URL or default
  const currentTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    
    if (isAdmin) {
      // Validate admin tab
      if (tabParam && ADMIN_TABS.includes(tabParam as AdminTabKey)) {
        return tabParam;
      }
      return 'dashboard'; // Default for admin
    } else {
      // Validate PO tab
      if (tabParam && PO_TABS.includes(tabParam as POTabKey)) {
        return tabParam;
      }
      return 'certification'; // Default for PO
    }
  }, [searchParams, isAdmin]);
  
  // Handle tab change - update URL with unsaved changes check (A3.4)
  // Note: The modal is now shown in ChecklistTab component
  const handleTabChange = useCallback((key: string) => {
    // Check for unsaved changes when leaving checklist tab (Admin only)
    if (isAdmin && hasUnsavedChanges && currentTab === 'checklist' && key !== 'checklist') {
      // Set pending tab - ChecklistTab will show modal
      setPendingTabKey(key);
      return;
    }
    // Exit remind-all mode when leaving dashboard tab
    if (isRemindAllMode) {
      setIsRemindAllMode(false);
    }
    setSearchParams({ tab: key }, { replace: true });
  }, [setSearchParams, isAdmin, hasUnsavedChanges, currentTab, isRemindAllMode]);
  
  // Confirm tab change - proceed with navigation and clear draft
  const handleConfirmTabChange = useCallback(() => {
    // Clear draft checklist before leaving
    dispatch(resetDraftChecklist());
    
    if (pendingTabKey) {
      setSearchParams({ tab: pendingTabKey }, { replace: true });
      setPendingTabKey(null);
    }
  }, [pendingTabKey, setSearchParams, dispatch]);
  
  // Cancel tab change - stay on current tab
  const handleCancelTabChange = useCallback(() => {
    setPendingTabKey(null);
  }, []);
  
  // Fetch process owners
  const fetchProcessOwners = useCallback(async () => {
    if (!selectedReport || !user) return;
    
    try {
      // console.log('🔄 Fetching process owners...');
      const owners = await getProcessOwners(user.companyId);
      dispatch(setProcessOwners({ owners, reportId: selectedReport.annualReportId }));
      
      // Auto-select first process owner if none selected
      if (owners.length > 0 && !selectedProcessOwnerId) {
        dispatch(setSelectedProcessOwner(owners[0].id));
      }
      
      // console.log(`✅ Process owners loaded: ${owners.length} items`);
    } catch (error) {
      console.error('❌ Error fetching process owners:', error);
      message.error('Failed to load process owners');
    }
  }, [selectedReport, user, isAdmin, dispatch]);
  
  // Initialize data on mount - fetch process owners list
  useEffect(() => {
    if (!selectedReport || !user) return;
    
    const reportId = selectedReport.annualReportId;
    const isNewReport = currentReportIdRef.current !== reportId;
    
    if (isNewReport) {
      // console.log(`🆕 Report changed: ${currentReportIdRef.current} → ${reportId}`);
      hasFetchedRef.current = false;
      currentReportIdRef.current = reportId;
    }
    
    // Skip if already fetched for this report or have cached data
    if (hasFetchedRef.current) {
      // console.log('⏭️ Skipping duplicate process owners fetch');
      return;
    }
    
    if (lastFetchedReportId === reportId && processOwners.length > 0) {
      // console.log('✅ Using cached process owners data');
      hasFetchedRef.current = true;
      return;
    }
    
    // Mark as fetched before fetching to prevent duplicates
    hasFetchedRef.current = true;
    
    const initializeData = async () => {
      setIsLoading(true);
      
      try {
        await fetchProcessOwners();
        
        // Set default quarter
        if (!selectedQuarter) {
          dispatch(setSelectedQuarter(getCurrentQuarter()));
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeData();
  }, [selectedReport, user, lastFetchedReportId, processOwners.length, selectedQuarter, fetchProcessOwners, dispatch]);
  
  // Fetch quarter data and checklist items when process owner or quarter changes
  // This centralizes data fetching for CertificationDetailTab and ChecklistTab
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedReport || !selectedProcessOwnerId || !selectedQuarter) {
        return;
      }
      
      // Create cache key to prevent duplicate calls
      const cacheKey = `${selectedReport.annualReportId}-${selectedProcessOwnerId}-${selectedQuarter}`;
      if (lastQuarterFetchRef.current === cacheKey) {
        // console.log('⏭️ CertificationPage: Skipping duplicate data fetch');
        return;
      }
      
      // Set loading and clear data to trigger loading state in child components
      setIsLoading(true);
      dispatch(setQuarterData(null));
      lastQuarterFetchRef.current = cacheKey;
      
      try {
        // console.log('🔄 CertificationPage: Fetching quarter data...', {
        //   annualReportId: selectedReport.annualReportId,
        //   processOwnerId: selectedProcessOwnerId,
        //   quarter: selectedQuarter,
        // });
        
        // Step 1: Fetch quarter data
        const quarterData = await getCertificationQuarterData(
          selectedReport.annualReportId,
          selectedProcessOwnerId,
          selectedQuarter
        );
        
        dispatch(setQuarterData(quarterData));
        // console.log('✅ CertificationPage: Quarter data loaded');
        
        // Step 2: Fetch checklist items if quarterData has certificationQuarterId
        if (quarterData?.certificationQuarterId) {
          // console.log('🔄 CertificationPage: Fetching checklist items...');
          const checklistItems = await getChecklistItems(
            selectedReport.annualReportId,
            selectedProcessOwnerId,
            selectedQuarter,
            quarterData.certificationQuarterId
          );
          dispatch(setChecklistItems(checklistItems));
          // console.log('✅ CertificationPage: Checklist items loaded:', checklistItems.length);
        } else {
          // Clear checklist if no certificationQuarterId
          dispatch(setChecklistItems([]));
        }
      } catch (error) {
        console.error('❌ CertificationPage: Error fetching data:', error);
        dispatch(setQuarterData(null));
        dispatch(setChecklistItems([]));
        // Reset cache on error to allow retry
        lastQuarterFetchRef.current = null;
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedReport, selectedProcessOwnerId, selectedQuarter, dispatch]);
  
  // Handle quarter change
  const handleQuarterChange = (quarter: Quarter) => {
    // Clear checklist data to prevent showing old quarter data while loading new
    dispatch(clearChecklistData());
    dispatch(setSelectedQuarter(quarter));
  };
  
  // Handle process owner change (Admin only)
  const handleProcessOwnerChange = (ownerId: string) => {
    // Clear all certification data to prevent showing old process owner data while loading new
    dispatch(clearCertificationData());
    dispatch(setSelectedProcessOwner(ownerId));
  };
  
  // Get selected process owner data
  const selectedProcessOwner = useMemo(() => {
    return processOwners.find(po => po.id === selectedProcessOwnerId) || null;
  }, [processOwners, selectedProcessOwnerId]);
  
  // Handle export trigger
  const handleExportClick = useCallback(() => {
    setTriggerExport(true);
  }, []);
  
  const handleExportClose = useCallback(() => {
    setTriggerExport(false);
  }, []);
  
  const handleExitRemindAll = useCallback(() => {
    setIsRemindAllMode(false);
  }, []);
  
  // ========================================
  // Admin Tabs Configuration
  // Tab 1: Dashboard - List all Process Owners with certification status matrix
  // Tab 2: Certification Details - View/edit attestation for selected PO
  // Tab 3: Documents - Document repository for selected PO
  // Tab 4: Change Indicators Config - Configure checklist for selected PO
  // ========================================
  const adminTabs = [
    {
      key: 'dashboard',
      label: (
        <TabLabel>
          <ListChecksIcon />
          Dashboard
        </TabLabel>
      ),
      children: (
        <AdminDashboardTab
          filterProcessOwnerId={filterProcessOwnerId}
          triggerExport={currentTab === 'dashboard' ? triggerExport : false}
          onExportClose={handleExportClose}
          isRemindAllMode={isRemindAllMode}
          onExitRemindAll={handleExitRemindAll}
        />
      ),
    },
    {
      key: 'certification',
      label: (
        <TabLabel>
          <AcknowledgmentIcon />
          Certification
        </TabLabel>
      ),
      children: (
        <CertificationDetailTab
          selectedQuarter={selectedQuarter}
          onQuarterChange={handleQuarterChange}
          isAdmin={true}
          selectedProcessOwner={selectedProcessOwner}
          processOwners={processOwners}
          onProcessOwnerChange={handleProcessOwnerChange}
          isLoading={isLoading}
        />
      ),
    },
    {
      key: 'documents',
      label: (
        <TabLabel>
          <DocumentIcon />
          Documents
        </TabLabel>
      ),
      children: (
        <DocumentsTab
          processOwnerId={selectedProcessOwnerId}
          isAdmin={true}
          selectedProcessOwner={selectedProcessOwner}
          processOwners={processOwners}
          onProcessOwnerChange={handleProcessOwnerChange}
        />
      ),
    },
    {
      key: 'checklist',
      label: (
        <TabLabel>
          <ChecklistIcon />
          Change Indicators
        </TabLabel>
      ),
      children: (
        <ChecklistTab
          selectedQuarter={selectedQuarter}
          onQuarterChange={handleQuarterChange}
          isAdmin={true}
          selectedProcessOwner={selectedProcessOwner}
          processOwners={processOwners}
          onProcessOwnerChange={handleProcessOwnerChange}
          isLoading={isLoading}
          pendingTabChange={pendingTabKey}
          onConfirmTabChange={handleConfirmTabChange}
          onCancelTabChange={handleCancelTabChange}
          triggerExport={currentTab === 'checklist' ? triggerExport : false}
          onExportClose={handleExportClose}
        />
      ),
    },
  ];
  
  // ========================================
  // Process Owner Tabs Configuration
  // Tab 1: Certification Review - View attestation and submit certification
  // Tab 2: Documents - View own document repository
  // Tab 3: Change Indicators Response - Respond to checklist questions
  // Tab 4: Overview - Process certification history matrix
  // ========================================
  const processOwnerTabs = [
    {
      key: 'certification',
      label: (
        <TabLabel>
          <AcknowledgmentIcon />
          Certification
        </TabLabel>
      ),
      children: (
        <CertificationDetailTab
          selectedQuarter={selectedQuarter}
          onQuarterChange={handleQuarterChange}
          isAdmin={false}
          selectedProcessOwner={selectedProcessOwner}
          processOwners={processOwners}
          onProcessOwnerChange={handleProcessOwnerChange}
          isLoading={isLoading}
        />
      ),
    },
    {
      key: 'documents',
      label: (
        <TabLabel>
          <DocumentIcon />
          Documents
        </TabLabel>
      ),
      children: (
        <DocumentsTab
          processOwnerId={selectedProcessOwnerId}
          isAdmin={false}
          selectedProcessOwner={selectedProcessOwner}
          processOwners={processOwners}
          onProcessOwnerChange={handleProcessOwnerChange}
        />
      ),
    },
    {
      key: 'checklist',
      label: (
        <TabLabel>
          <ChecklistIcon />
          Change Indicators
        </TabLabel>
      ),
      children: (
        <ChecklistTab
          selectedQuarter={selectedQuarter}
          onQuarterChange={handleQuarterChange}
          isAdmin={false}
          selectedProcessOwner={selectedProcessOwner}
          processOwners={processOwners}
          onProcessOwnerChange={handleProcessOwnerChange}
          isLoading={isLoading}
          pendingTabChange={pendingTabKey}
          onConfirmTabChange={handleConfirmTabChange}
          onCancelTabChange={handleCancelTabChange}
          triggerExport={currentTab === 'checklist' ? triggerExport : false}
          onExportClose={handleExportClose}
        />
      ),
    },
    {
      key: 'overview',
      label: (
        <TabLabel>
          <ArchiveIcon />
          Overview
        </TabLabel>
      ),
      children: <POOverviewTab />,
    },
  ];
  
  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>
            {isAdmin ? 'Quarterly ICFR Process Owner Certification' : 'Process Owner Certification'}
          </PageTitle>
        </PageHeader>
        <LoadingContainer>
          <div className="spinner" />
        </LoadingContainer>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>
          {isAdmin ? 'Quarterly ICFR Process Owner Certification' : 'Process Owner Certification'}
        </PageTitle>
        <HeaderActions>
          {isAdmin && filterProcessOwnerId && (
            <Tooltip title="Clear filter">
              <FilterButtonWrapper onClick={handleClearFilter}>
                <FilterOutlined />
                <FilterBadge />
              </FilterButtonWrapper>
            </Tooltip>
          )}
          {currentTab === 'dashboard' && isAdmin && (
            isRemindAllMode ? (
              <Button
                icon={<MailOutlined />}
                onClick={() => setIsRemindAllMode(false)}
                style={{ height: 40, borderRadius: 6, fontFamily: 'Inter', fontWeight: 500 }}
              >
                Cancel Remind
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={() => setIsRemindAllMode(true)}
                style={{ height: 40, borderRadius: 6, fontFamily: 'Inter', fontWeight: 500 }}
              >
                Remind All
              </Button>
            )
          )}
          {(currentTab === 'dashboard' || currentTab === 'checklist') && (
            <ExportButton onClick={handleExportClick} />
          )}
          {/* {selectedReport && isAdmin && (
            <ChangeLogButton
              actionType="certification"
              annualReportId={selectedReport.annualReportId}
            />
          )} */}
        </HeaderActions>
      </PageHeader>

      <StyledTabs
        activeKey={currentTab}
        onChange={handleTabChange}
        items={isAdmin ? adminTabs : processOwnerTabs}
        destroyInactiveTabPane={false}
      />
      {/* Note: Unsaved changes modal is now handled in ChecklistTab component */}
    </PageContainer>
  );
};

export default CertificationPage;
